import { useState } from 'react';
import moment from 'moment-jalaali';
import FeedbackForm from './FeedbackForm';

export default function ReportCard({ report, onFeedbackSubmitted }) {
  const [isEditing, setIsEditing] = useState(false);

  // اصلاح تاریخ: اولویت با فیلد date، در غیر این صورت createdAt
  const reportDate = report.date || report.createdAt;
  const dateStr = moment(reportDate).format('jYYYY/jMM/jDD');
  const dayName = moment(reportDate).format('dddd');

  const photos = [
    ...(report.data?.map((d) => d.url) || []),
    ...(report.extraPhotos || []),
  ];

  const hasFeedback = !!report.coachFeedback?.comment;

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 border-2 border-blue-50 relative overflow-hidden">
      {/* Badge برای گزارش جدید */}
      <div className="absolute top-0 left-0 bg-blue-600 text-white text-[10px] px-3 py-1 rounded-br-lg shadow-sm">
        جدیدترین گزارش
      </div>

      {/* Header */}
      <div className="flex justify-between items-end mb-6 mt-2">
        <div>
          <h4 className="font-black text-xl text-gray-800">گزارش {dateStr}</h4>
          <p className="text-sm text-blue-500 font-medium">
            {dayName} •{' '}
            {report.type === 'weekly' ? 'وضعیت هفتگی' : 'وضعیت روزانه'}
          </p>
        </div>
        <div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-bold ${
              hasFeedback
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
            {hasFeedback ? '✓ فیدبک داده شده' : '⏳ در انتظار فیدبک'}
          </span>
        </div>
      </div>

      {/* نمایش شاخص‌ها (Metrics) */}
      <Metrics report={report} />

      {/* یادداشت کاربر */}
      {report.note && <UserNote note={report.note} />}

      {/* تصاویر */}
      {photos.length > 0 && (
        <div className="mb-6">
          <h5 className="text-sm font-bold mb-3 text-gray-600 font-vazir">
            تصاویر ارسالی:
          </h5>
          <PhotoGrid photos={photos} />
        </div>
      )}

      {/* بخش فیدبک */}
      <div className="mt-8 pt-6 border-t border-dashed border-gray-200">
        {!hasFeedback && !isEditing && (
          <FeedbackForm
            reportId={report._id}
            onSubmitSuccess={onFeedbackSubmitted}
          />
        )}

        {hasFeedback && !isEditing && (
          <div className="relative">
            <FeedbackReadOnly feedback={report.coachFeedback} />
            <button
              onClick={() => setIsEditing(true)}
              className="absolute top-4 left-4 px-4 py-2 text-xs bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors rounded-xl shadow-sm font-bold">
              ویرایش پاسخ مربی
            </button>
          </div>
        )}

        {hasFeedback && isEditing && (
          <FeedbackForm
            reportId={report._id}
            onSubmitSuccess={() => {
              onFeedbackSubmitted();
              setIsEditing(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

// --- توابع کمکی که باعث Error شده بودند ---

function Metrics({ report }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
      <Box label="میانگین کالری" value={report.avgCalories} unit="kcal" />
      <Box label="پروتئین" value={report.macros?.protein} unit="%" />
      <Box label="کربوهیدرات" value={report.macros?.carbs} unit="%" />
      <Box label="چربی" value={report.macros?.fat} unit="%" />
      <Box label="میانگین قدم" value={report.avgSteps} />
      <Box label="قدرتی" value={report.strengthDays} unit="روز" />
      <Box label="هوازی" value={report.cardioDays} unit="روز" />
    </div>
  );
}

function Box({ label, value, unit = '' }) {
  return (
    <div className="bg-gray-50 p-3 rounded-2xl text-center border border-gray-100 shadow-sm">
      <div className="text-[10px] text-gray-500 mb-1 font-medium">{label}</div>
      <div className="font-black text-gray-800 text-sm">
        {value ?? '-'}{' '}
        <span className="text-[10px] font-normal text-gray-400">{unit}</span>
      </div>
    </div>
  );
}

function PhotoGrid({ photos }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {photos.map((src, i) => (
        <ZoomableImage key={i} src={src} />
      ))}
    </div>
  );
}

function ZoomableImage({ src }) {
  const [isHover, setIsHover] = useState(false);
  const [origin, setOrigin] = useState('center center');

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border h-40 w-full shadow-sm bg-gray-100"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onMouseMove={handleMove}>
      <img
        src={src}
        alt="progress"
        style={{
          transformOrigin: origin,
          transform: isHover ? 'scale(2.2)' : 'scale(1)',
          transition: isHover ? 'none' : 'transform 0.3s ease-out',
        }}
        className="object-contain h-full w-full"
      />
    </div>
  );
}

function UserNote({ note }) {
  return (
    <div className="bg-amber-50 rounded-xl p-4 mb-6 text-sm border-r-4 border-amber-400">
      <strong className="text-amber-800 block mb-1">توضیحات کاربر:</strong>
      <p className="text-gray-700 leading-relaxed">{note}</p>
    </div>
  );
}

function FeedbackReadOnly({ feedback }) {
  return (
    <div className="bg-blue-50 border border-blue-100 p-5 rounded-2xl">
      <h4 className="font-black text-blue-700 mb-4 flex items-center gap-2">
        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
        آخرین فیدبک ثبت شده شما
      </h4>

      <div className="mb-4">
        <span className="text-xs text-gray-500">امتیاز شما به این گزارش:</span>
        <span className="mr-2 font-bold text-blue-600">
          {feedback.score} از 10
        </span>
      </div>

      <div className="mb-6">
        <strong className="text-xs text-gray-500 block mb-1">
          توضیحات شما:
        </strong>
        <p className="text-gray-700 text-sm italic">"{feedback.comment}"</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Box
          label="کالری دوره بعد"
          value={feedback.nextPeriodPlan?.dailyCalories}
        />
        <Box
          label="پروتئین (%)"
          value={feedback.nextPeriodPlan?.proteinPercent}
        />
        <Box
          label="کربوهیدرات (%)"
          value={feedback.nextPeriodPlan?.carbsPercent}
        />
        <Box label="چربی (%)" value={feedback.nextPeriodPlan?.fatPercent} />
        <Box
          label="هدف قدم"
          value={feedback.nextPeriodPlan?.dailyStepsTarget}
        />
        <Box label="هدف فیبر" value={feedback.nextPeriodPlan?.fiberTarget} />
        <Box
          label="روز تمرین"
          value={feedback.nextPeriodPlan?.trainingDaysTarget}
        />
      </div>
    </div>
  );
}
